/**
 * Supplier Quote Email Parser Service
 * Intelligently extracts structured quote data from supplier email responses
 * Similar to emailContentParser.ts but specialized for quote responses
 */

// Interface for parsed supplier quote content
interface ParsedSupplierQuote {
  supplierInfo: {
    supplier_email: string;
    supplier_name?: string;
    company?: string;
    contact_person?: string;
    phone?: string;
  };
  quoteInfo: {
    total_quote_value?: number;
    currency?: string;
    delivery_time_days?: number;
    quote_valid_until?: string;
    payment_terms?: string;
    delivery_terms?: string;
    warranty_terms?: string;
    special_conditions?: string;
    notes?: string;
  };
  positions: {
    position_number: string;
    part_number?: string;
    part_name?: string;
    quantity?: number;
    unit_price?: number;
    total_price?: number;
    delivery_days?: number;
    supplier_notes?: string;
  }[];
  extractionMetadata: {
    confidence: 'high' | 'medium' | 'low';
    extractedFields: string[];
    detectedLanguage: 'de' | 'en' | 'unknown';
    positionCount: number;
    totalValueFound: boolean;
    deliveryTimeFound: boolean;
  };
}

class SupplierQuoteEmailParser {
  
  /**
   * Parse supplier quote email content and extract structured data
   */
  static parseSupplierQuote(emailContent: {
    from_address: string;
    subject: string;
    body_text?: string;
    body_html?: string;
  }): ParsedSupplierQuote {
    
    const content = emailContent.body_text || emailContent.body_html || '';
    const subject = emailContent.subject || '';
    
    // Initialize result structure
    const result: ParsedSupplierQuote = {
      supplierInfo: {
        supplier_email: emailContent.from_address
      },
      quoteInfo: {
        currency: 'EUR' // Default
      },
      positions: [],
      extractionMetadata: {
        confidence: 'low',
        extractedFields: ['supplier_email'],
        detectedLanguage: this.detectLanguage(content),
        positionCount: 0,
        totalValueFound: false,
        deliveryTimeFound: false
      }
    };

    // Extract supplier information
    result.supplierInfo = {
      ...result.supplierInfo,
      ...this.extractSupplierInfo(content, emailContent.from_address)
    };

    // Extract quote information
    result.quoteInfo = {
      ...result.quoteInfo,
      ...this.extractQuoteInfo(content, subject)
    };

    // Extract quoted positions (most important!)
    result.positions = this.extractQuotedPositions(content);
    result.extractionMetadata.positionCount = result.positions.length;

    // Calculate confidence and extracted fields
    result.extractionMetadata = this.calculateExtractionMetadata(result);

    return result;
  }

  /**
   * Detect email language (German/English)
   */
  private static detectLanguage(content: string): 'de' | 'en' | 'unknown' {
    const germanKeywords = ['angebot', 'preis', 'lieferzeit', 'lieferung', 'versand', 'zahlungsbedingungen', 'mit freundlichen grüßen'];
    const englishKeywords = ['quote', 'price', 'delivery', 'shipping', 'payment terms', 'best regards', 'quotation'];
    
    const lowerContent = content.toLowerCase();
    const germanScore = germanKeywords.reduce((score, word) => 
      score + (lowerContent.includes(word) ? 1 : 0), 0);
    const englishScore = englishKeywords.reduce((score, word) => 
      score + (lowerContent.includes(word) ? 1 : 0), 0);
    
    if (germanScore > englishScore) return 'de';
    if (englishScore > germanScore) return 'en';
    return 'unknown';
  }

  /**
   * Extract supplier information from email content
   */
  private static extractSupplierInfo(content: string, fromEmail: string): Partial<ParsedSupplierQuote['supplierInfo']> {
    const info: Partial<ParsedSupplierQuote['supplierInfo']> = {};

    // Extract company name patterns
    const companyPatterns = [
      /(?:^|\n)([A-ZÄÖÜ][a-zA-ZäöüÄÖÜß\s]+(?:GmbH|AG|KG|OHG|e\.V\.|Inc\.|LLC|Ltd\.|Corp\.?))/gm,
      /Mit freundlichen Grüßen[\s\n]*([A-ZÄÖÜ][a-zA-ZäöüÄÖÜß\s]+(?:GmbH|AG|KG))/i,
      /Best regards[\s\n]*([A-Z][a-zA-Z\s]+(?:Inc\.|LLC|Ltd\.|Corp\.))/i
    ];

    for (const pattern of companyPatterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        info.company = match[1].trim();
        break;
      }
    }

    // Extract contact person name
    const namePatterns = [
      /Mit freundlichen Grüßen[\s\n]*([A-ZÄÖÜ][a-zA-ZäöüÄÖÜß]+\s+[A-ZÄÖÜ][a-zA-ZäöüÄÖÜß]+)/i,
      /Best regards[\s\n]*([A-Z][a-zA-Z]+\s+[A-Z][a-zA-Z]+)/i,
      /([A-ZÄÖÜ][a-zA-ZäöüÄÖÜß]+\s+[A-ZÄÖÜ][a-zA-ZäöüÄÖÜß]+)[\s\n]*(?:Sales|Verkauf|Vertrieb)/i
    ];

    for (const pattern of namePatterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        info.contact_person = match[1].trim();
        break;
      }
    }

    // Extract phone number
    const phonePattern = /(?:Tel|Phone|Telefon)[:\s]*(\+?\d+[\s\-\d]+)/i;
    const phoneMatch = content.match(phonePattern);
    if (phoneMatch) {
      info.phone = phoneMatch[1].trim();
    }

    // Extract supplier name from company or contact person
    if (!info.supplier_name) {
      info.supplier_name = info.company || info.contact_person || fromEmail.split('@')[0];
    }

    return info;
  }

  /**
   * Extract quote information (pricing, terms, etc.)
   */
  private static extractQuoteInfo(content: string, subject: string): Partial<ParsedSupplierQuote['quoteInfo']> {
    const info: Partial<ParsedSupplierQuote['quoteInfo']> = {};

    // Extract total quote value
    const totalPatterns = [
      /(?:Gesamtpreis|Total|Gesamt|Gesamtsumme)[\s:]*([€$]?\s*[\d.,]+)\s*(?:€|EUR|USD|\$)?/gi,
      /(?:Total Price|Total Cost|Grand Total)[\s:]*([€$]?\s*[\d.,]+)\s*(?:€|EUR|USD|\$)?/gi,
      /(?:€|EUR|USD|\$)\s*([\d.,]+)[\s]*(?:gesamt|total|insgesamt)/gi
    ];

    for (const pattern of totalPatterns) {
      const match = pattern.exec(content);
      if (match && match[1]) {
        const price = parseFloat(match[1].replace(/[,.]/g, m => m === ',' ? '.' : ''));
        if (!isNaN(price) && price > 0) {
          info.total_quote_value = price;
          break;
        }
      }
    }

    // Extract delivery time
    const deliveryPatterns = [
      /(?:Lieferzeit|Delivery|Lieferung)[\s:]*(\d+)[\s]*(?:Tage|days|Wochen|weeks)/gi,
      /(\d+)[\s]*(?:Tage|days)[\s]*(?:Lieferzeit|delivery|Versand)/gi,
      /(?:Lead time|Delivery time)[\s:]*(\d+)[\s]*(?:days|weeks)/gi
    ];

    for (const pattern of deliveryPatterns) {
      const match = pattern.exec(content);
      if (match && match[1]) {
        let days = parseInt(match[1]);
        if (content.toLowerCase().includes('woche') || content.toLowerCase().includes('week')) {
          days *= 7; // Convert weeks to days
        }
        info.delivery_time_days = days;
        break;
      }
    }

    // Extract currency
    if (content.includes('€') || content.toLowerCase().includes('eur')) {
      info.currency = 'EUR';
    } else if (content.includes('$') || content.toLowerCase().includes('usd')) {
      info.currency = 'USD';
    }

    // Extract quote validity
    const validityPatterns = [
      /(?:Angebot gültig bis|Valid until|Quote valid until)[\s:]*(\d{1,2}\.?\s*\d{1,2}\.?\s*\d{4})/i,
      /(\d{1,2}\.?\s*\d{1,2}\.?\s*\d{4})[\s]*(?:gültig|valid)/i
    ];

    for (const pattern of validityPatterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        try {
          const dateStr = match[1].replace(/\s/g, '');
          const [day, month, year] = dateStr.split('.');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          info.quote_valid_until = date.toISOString().split('T')[0];
        } catch (e) {
          // Invalid date format, skip
        }
        break;
      }
    }

    // Extract payment terms
    const paymentPatterns = [
      /(?:Zahlungsbedingungen|Payment terms?)[\s:]*([^.\n\r]+)/i,
      /(\d+)[\s]*(?:Tage|days)[\s]*(?:netto|net)/i
    ];

    for (const pattern of paymentPatterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        info.payment_terms = match[1].trim();
        break;
      }
    }

    // Extract delivery terms
    const deliveryTermsPatterns = [
      /(?:Lieferbedingungen|Delivery terms?)[\s:]*([^.\n\r]+)/i,
      /(EXW|FOB|CIF|DDP)[\s]*[^.\n\r]*/i
    ];

    for (const pattern of deliveryTermsPatterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        info.delivery_terms = match[1].trim();
        break;
      }
    }

    // Extract warranty information
    const warrantyPatterns = [
      /(?:Garantie|Warranty)[\s:]*([^.\n\r]+)/i,
      /(\d+)[\s]*(?:Monate|months)[\s]*(?:Garantie|warranty)/i
    ];

    for (const pattern of warrantyPatterns) {
      const match = content.match(pattern);
      if (match && match[1]) {
        info.warranty_terms = match[1].trim();
        break;
      }
    }

    // Extract notes from email (clean content)
    const cleanContent = content
      .replace(/Mit freundlichen Grüßen[\s\S]*$/i, '') // Remove signature
      .replace(/Best regards[\s\S]*$/i, '')
      .replace(/\n{2,}/g, '\n\n') // Normalize line breaks
      .trim();
    
    if (cleanContent.length > 50) {
      info.notes = cleanContent.substring(0, 500) + (cleanContent.length > 500 ? '...' : '');
    }

    return info;
  }

  /**
   * Extract quoted positions from email content (MOST IMPORTANT FUNCTION)
   */
  private static extractQuotedPositions(content: string): ParsedSupplierQuote['positions'] {
    const positions: ParsedSupplierQuote['positions'] = [];

    // German position patterns for quotes
    const germanPatterns = [
      /Position\s*(\d+)[:\s]*([^:\n]+?)[\s\n]*(?:Preis|Price)[:\s]*€?\s*([\d.,]+)[\s]*€?[\s\n]*(?:Lieferzeit|Delivery)[:\s]*(\d+)[\s]*(?:Tage|days)?/gi,
      /(\d+)\.\s*([^:\n]+?)[\s\n]*€?\s*([\d.,]+)[\s]*€?[\s\n]*(\d+)[\s]*(?:Tage|days)/gi,
      /(?:Pos\.?\s*(\d+)|Position\s*(\d+))[:\s-]*([^€\n]+?)[\s]*€?\s*([\d.,]+)[\s]*€?/gi
    ];

    // English position patterns for quotes
    const englishPatterns = [
      /Position\s*(\d+)[:\s]*([^:\n]+?)[\s\n]*(?:Price)[:\s]*\$?\s*([\d.,]+)[\s]*\$?[\s\n]*(?:Delivery)[:\s]*(\d+)[\s]*(?:days)?/gi,
      /(\d+)\.\s*([^:\n]+?)[\s\n]*\$?\s*([\d.,]+)[\s]*\$?[\s\n]*(\d+)[\s]*days/gi
    ];

    // Try German patterns first
    let positionNumber = 1;
    for (const pattern of germanPatterns) {
      let match;
      while ((match = pattern.exec(content)) !== null) {
        const posNum = parseInt(match[1] || match[2]) || positionNumber;
        const partName = (match[2] || match[3] || '').trim();
        const unitPrice = match[3] ? parseFloat(match[3].replace(',', '.')) : 0;
        const deliveryDays = match[4] ? parseInt(match[4]) : undefined;

        if (partName && unitPrice > 0) {
          positions.push({
            position_number: posNum.toString(),
            part_name: partName,
            unit_price: unitPrice,
            total_price: unitPrice, // Will be calculated with quantity later
            delivery_days: deliveryDays,
            supplier_notes: ''
          });
          positionNumber++;
        }
      }
      // Reset regex lastIndex for next pattern
      pattern.lastIndex = 0;
    }

    // If no positions found with German patterns, try English
    if (positions.length === 0) {
      for (const pattern of englishPatterns) {
        let match;
        while ((match = pattern.exec(content)) !== null) {
          const posNum = parseInt(match[1]) || positionNumber;
          const partName = (match[2] || '').trim();
          const unitPrice = match[3] ? parseFloat(match[3].replace(',', '.')) : 0;
          const deliveryDays = match[4] ? parseInt(match[4]) : undefined;

          if (partName && unitPrice > 0) {
            positions.push({
              position_number: posNum.toString(),
              part_name: partName,
              unit_price: unitPrice,
              total_price: unitPrice,
              delivery_days: deliveryDays,
              supplier_notes: ''
            });
            positionNumber++;
          }
        }
        pattern.lastIndex = 0;
      }
    }

    // Fallback: Look for simple price lists
    if (positions.length === 0) {
      const simplePatterns = [
        /(?:^|\n)[-*•]\s*([^:\n€$]+?)[\s]*€?\s*([\d.,]+)[\s]*€?/gim,
        /([^:\n€$]+?)[\s]*[-:]\s*€?\s*([\d.,]+)[\s]*€?/gi
      ];

      for (const pattern of simplePatterns) {
        let match;
        while ((match = pattern.exec(content)) !== null && positions.length < 10) {
          const partName = match[1].trim();
          const unitPrice = parseFloat(match[2].replace(',', '.'));

          if (partName && unitPrice > 0 && partName.length > 3) {
            positions.push({
              position_number: (positions.length + 1).toString(),
              part_name: partName,
              unit_price: unitPrice,
              total_price: unitPrice,
              supplier_notes: ''
            });
          }
        }
        pattern.lastIndex = 0;
      }
    }

    return positions.slice(0, 20); // Limit to 20 positions max
  }

  /**
   * Calculate extraction metadata (confidence, etc.)
   */
  private static calculateExtractionMetadata(result: ParsedSupplierQuote): ParsedSupplierQuote['extractionMetadata'] {
    const extractedFields: string[] = ['supplier_email']; // Always have email
    let confidence: 'high' | 'medium' | 'low' = 'low';

    // Count extracted fields
    if (result.supplierInfo.company) extractedFields.push('company');
    if (result.supplierInfo.contact_person) extractedFields.push('contact_person');
    if (result.quoteInfo.total_quote_value) extractedFields.push('total_quote_value');
    if (result.quoteInfo.delivery_time_days) extractedFields.push('delivery_time_days');
    if (result.quoteInfo.payment_terms) extractedFields.push('payment_terms');
    if (result.positions.length > 0) extractedFields.push('positions');

    // Calculate confidence based on extracted data quality
    const hasPositions = result.positions.length > 0;
    const hasPrices = result.positions.some(p => (p.unit_price || 0) > 0);
    const hasTotalValue = !!result.quoteInfo.total_quote_value;
    const hasDeliveryTime = !!result.quoteInfo.delivery_time_days;
    const hasCompany = !!result.supplierInfo.company;

    result.extractionMetadata.totalValueFound = hasTotalValue;
    result.extractionMetadata.deliveryTimeFound = hasDeliveryTime;

    if (hasPositions && hasPrices && (hasTotalValue || hasDeliveryTime) && extractedFields.length >= 5) {
      confidence = 'high';
    } else if (hasPositions && (hasPrices || hasTotalValue) && extractedFields.length >= 3) {
      confidence = 'medium';
    }

    return {
      confidence,
      extractedFields,
      detectedLanguage: result.extractionMetadata.detectedLanguage,
      positionCount: result.positions.length,
      totalValueFound: result.extractionMetadata.totalValueFound,
      deliveryTimeFound: result.extractionMetadata.deliveryTimeFound
    };
  }

  /**
   * Convert parsed data to QuoteManagementHub format
   */
  static convertToQuoteForm(
    parsedData: ParsedSupplierQuote
  ): Partial<any> {
    return {
      supplier_name: parsedData.supplierInfo.supplier_name || '',
      supplier_email: parsedData.supplierInfo.supplier_email,
      total_quote_value: parsedData.quoteInfo.total_quote_value || 0,
      currency: parsedData.quoteInfo.currency || 'EUR',
      delivery_time_days: parsedData.quoteInfo.delivery_time_days || 14,
      quote_valid_until: parsedData.quoteInfo.quote_valid_until || 
        new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      payment_terms: parsedData.quoteInfo.payment_terms || '30 Tage netto',
      delivery_terms: parsedData.quoteInfo.delivery_terms || 'EXW',
      warranty_terms: parsedData.quoteInfo.warranty_terms || '24 Monate',
      special_conditions: parsedData.quoteInfo.special_conditions || '',
      notes: parsedData.quoteInfo.notes || '',
      quoted_positions: parsedData.positions.map(pos => ({
        position_number: pos.position_number,
        part_number: pos.part_number || '',
        part_name: pos.part_name || '',
        quantity: pos.quantity || 1,
        unit_price: pos.unit_price || 0,
        total_price: pos.total_price || (pos.unit_price || 0) * (pos.quantity || 1),
        delivery_days: pos.delivery_days || parsedData.quoteInfo.delivery_time_days || 14,
        supplier_notes: pos.supplier_notes || ''
      }))
    };
  }
}

export { SupplierQuoteEmailParser, type ParsedSupplierQuote };
